/**
 * One project-level answer, at the bottom of Çekimler.
 *
 * Analiz and Kapsama used to be columns on every session row, which restated a
 * project-level fact once per session and made each row a small dashboard.
 * They belong here, once.
 *
 * **Every number is read, none is derived optimistically.** The analysis ratio
 * comes from `session_summary` on the project coverage analysis, which is the
 * backend's own count of how many sessions contributed to it - not from "an
 * analysis record exists, therefore analysis is done". The coverage line counts
 * the five *defined* regions by status; anatomy outside the coverage model is
 * named separately and never counted as a failure.
 *
 * A project with no coverage analysis yet is not a project that failed
 * analysis, and the two states read differently.
 */

import { Link } from "react-router-dom";

import { COVERAGE_REGIONS, type ProjectCoverage } from "@/api/types";
import { Section } from "@/components/product";
import { paths } from "@/app/paths";
import type { CaptureSummary } from "./useCaptureSummary";

/** `3 / 5 bölge yeterli`, plus whatever else the regions actually are. */
function coverageLines(coverage: ProjectCoverage): string[] {
  const regions = coverage.regions ?? {};
  const counts = { sufficient: 0, partial: 0, insufficient: 0, unknown: 0 };
  for (const region of COVERAGE_REGIONS) {
    const status = regions[region]?.status;
    if (status) counts[status] += 1;
  }
  const lines = [`${counts.sufficient} / ${COVERAGE_REGIONS.length} bölge yeterli`];
  if (counts.partial > 0) lines.push(`${counts.partial} kısmi`);
  if (counts.insufficient > 0) lines.push(`${counts.insufficient} yetersiz`);
  // Kept distinct from the three above: nobody could measure these, which is
  // an open question rather than a deficiency.
  if (counts.unknown > 0) lines.push(`${counts.unknown} ölçülemedi`);
  return lines;
}

export function CaptureSummarySection({
  projectId,
  sessionCount,
  summary,
  coverage,
  coverageMissing,
  coverageSupported = true,
  coverageKind = "spatial_viewpoint_v1",
}: {
  projectId: string;
  sessionCount: number;
  summary: CaptureSummary;
  /** The project coverage analysis, or null when there is not one yet. */
  coverage: ProjectCoverage | null;
  /** True when the API answered 404 - never computed, as opposed to failed. */
  coverageMissing: boolean;
  coverageSupported?: boolean;
  coverageKind?: string;
}) {
  const { totals, loading, failed } = summary;

  return (
    <Section title="Çekim Özeti">
      <div className="capturesummary">
        <div className="capturesummary__block">
          <h4 className="capturesummary__heading">Medya</h4>
          {loading ? (
            <p className="muted">Sayılıyor…</p>
          ) : (
            <ul className="capturesummary__counts">
              <li>
                <strong>{totals.photos}</strong> Fotoğraf
              </li>
              <li>
                <strong>{totals.videos}</strong> Video
              </li>
              <li>
                <strong>{totals.media}</strong> Medya
              </li>
            </ul>
          )}
          {/* A session whose media could not be read makes the total a lower
              bound, and saying so is cheaper than a wrong number. */}
          {failed.length > 0 && (
            <p className="muted small">
              {failed.length} çekimin dosyaları okunamadı; toplamlar eksik olabilir.
            </p>
          )}
        </div>

        <div className="capturesummary__block">
          <h4 className="capturesummary__heading">Analiz</h4>
          <p className="muted">
            {!coverageSupported
              ? "Konuya uygun çekim analizi etkin; kalite ölçülür, desteklenmeyen semantik sonuçlar üretilmez."
              : coverageMissing || coverage === null
              ? "Analiz henüz çalıştırılmadı."
              : coverage.session_summary.unanalyzed_sessions === 0
                ? "Analiz tamamlandı."
                : `${sessionCount} çekimden ${coverage.session_summary.analyzed_sessions}'i analiz edildi.`}
          </p>
        </div>

        <div className="capturesummary__block">
          <h4 className="capturesummary__heading">Kapsama</h4>
          {!coverageSupported ? (
            <p className="muted">{coverageKind === "human_body_capture_v1" ? "Vücut çekim kapsaması" : coverageKind === "object_viewpoint_v1" ? "Nesne görünüm kapsaması" : "Mekânsal görünüm kapsaması"}: henüz authoritative olarak değerlendirilmedi.</p>
          ) : coverageMissing || coverage === null ? (
            <p className="muted">Kapsama henüz ölçülmedi.</p>
          ) : (
            <ul className="capturesummary__counts">
              {coverageLines(coverage).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {coverage && (coverage.overall.unmeasured_areas?.length ?? 0) > 0 && (
            // Named, never counted as a failed region: these are outside the
            // coverage model entirely.
            <p className="muted small">
              Bazı alanlar bu sürümde değerlendirme kapsamı dışında.
            </p>
          )}
        </div>
      </div>

      {/* Both destinations exist; neither button is decoration. */}
      <div className="row">
        <Link className="btn btn--secondary" to={paths.projectCoverage(projectId)}>
          Kapsamayı Görüntüle
        </Link>
      </div>
    </Section>
  );
}
